import "./style.scss";

import { InputAdornment } from "@mui/material";
import Bank from "assets/img/svg/bank";
import Check from "assets/img/svg/check";
import Paypal from "assets/img/svg/paypal";
import Plus from "assets/img/svg/plus";
import type { TCurrencyData } from "components/AdminPanel/types";
import { getAccountingTypologyName } from "components/Beneficiar/utils";
import CustomFieldsCollapse from "components/shared/CustomFieldsCollapse";
import AutocompleteFormComponent from "components/shared/FormComponent/AutocompleteFormComponent";
import TextFormComponent from "components/shared/FormComponent/TextFormComponent";
import LargeSwitch from "components/shared/LargeSwitch";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import TitleSeparation from "components/shared/TitleSeparation";
import { FlagTag } from "components/TitleFormContainer/PriceChannelsStep";
import type { IBeneficiar } from "containers/Beneficiars/types";
import type { ROLES } from "containers/ScopeContext/roles.enum";
import { currenciesList } from "currenciesList";
import type { IsCompanyWithExceptions } from "hooks/useCompanySettings";
import { useCompanySettings } from "hooks/useCompanySettings";
import { usePermissions } from "hooks/usePermissions";
import CountryAutocomplete from "components/CountryAutocomplete";
import { deriveBankingMode } from "helpers/bankingStatusResolver";
import type { SetStateAction } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { IBrand } from "store/brandStore";
import type { ICustomField } from "store/customFieldsStore";
import type { ICompany } from "store/settingsStore";
import { useAppSelector } from "store/store";

import MessageBlock from "components/shared/MessageBlock";
import { updateBeneficiary } from "store/beneficiaryStore";
import { useAppDispatch } from "store/store";

import AccountingNumbersTableInput from "../AccountingNumbersTableInput";
import type { TFormCreateBeneficiar } from "../types";
import BankingFieldsSection, { type BankingMode } from "./BankingFieldsSection";
import BrandAccountingCodes from "./BrandAccountingCodes";

// ─── Private sub-components ───────────────────────────────────────────────────

interface IBankingStatusToggles {
    mode: "new" | "edit-page";
    beneficiaryId: number;
}

const BankAccountCountrySelector = ({ mode: _mode, beneficiaryId: _beneficiaryId }: IBankingStatusToggles) => {
    const [t] = useTranslation();
    const {
        control,
        formState: { errors },
        watch,
    } = useFormContext<TFormCreateBeneficiar>();
    const { hasPermissions } = usePermissions();

    const ribDisabled = !hasPermissions(["beneficiary:edit-rib"]);

    return (
        <CountryAutocomplete
            control={control}
            errors={errors}
            controlName="bankAccountCountry"
            label={t("IbanModal.BankAccountCountry")}
            disabled={ribDisabled}
        />
    );
};

// ─── Types ────────────────────────────────────────────────────────────────────

interface IAccountingPaymentStep {
    /**
     * React useForm() hooks
     */
    stepTitle: string;
    stepSubtitle: string;
    indexStep: number;
    /**
     * Beneficiar accepted payments (bank transfert, paypal, bankcheck)
     */
    acceptedPayments: string[];
    /**
     * Set state accepted payment, used with toggle buttons on payments part
     */
    setAcceptedPayments: React.Dispatch<SetStateAction<string[]>>;
    /** Form mode */
    mode: "new" | "edit-page";
    /**
     * Beneficiary data
     */
    beneficiaryData: IBeneficiar;
    /**
     * condition to display rib error
     */
    showRibError: boolean;
    /**
     * change rib error state
     */
    setShowRibError: React.Dispatch<SetStateAction<boolean>>;
    /**
     * All properties and data related to the company.
     */
    companyInfos: ICompany | undefined;
    /**
     * Whether company uses multiple currencies.
     */
    usesMultipleCurrencies: boolean;
    /**
     * Other currencies chosen by company
     */
    alternativeCurrencies: string[];
    /**
     * Does company uses multiple brands.
     */
    usesBrands: boolean;
    /**
     * The company's brands.
     */
    brands: IBrand[];
    /**
     * Array with fields belonging to the section
     */
    customFields: ICustomField[];
    /**
     * Company accepted payments
     */
    companyAcceptedPayments: string;
    /**
     * Is company with exceptions
     */
    isCompany: IsCompanyWithExceptions;
    /**
     * Role
     */
    userRole: ROLES;
}

// ─── Main component ───────────────────────────────────────────────────────────

const AccountingPaymentStep = ({
    stepTitle,
    stepSubtitle,
    indexStep,
    acceptedPayments,
    setAcceptedPayments,
    mode,
    beneficiaryData,
    setShowRibError,
    companyInfos,
    usesMultipleCurrencies,
    alternativeCurrencies,
    usesBrands,
    brands,
    customFields,
    companyAcceptedPayments,
}: IAccountingPaymentStep) => {
    const [t] = useTranslation();
    const {
        control,
        formState: { errors },
        watch,
        clearErrors,
        setValue,
        resetField,
    } = useFormContext<TFormCreateBeneficiar>();

    const { typologies } = useAppSelector((state) => state.accounting);
    const { getCompanySettings, countryFeatures, internationalBankingEnabled, companyCountryCode } =
        useCompanySettings();
    const { hasPermissions } = usePermissions();

    // Banking mode derived from bankingStatus form field
    const bankingStatus = watch("bankingStatus");
    const bankAccountCountry = watch("bankAccountCountry");

    const bankingMode: BankingMode = (() => {
        if (!internationalBankingEnabled) return "local";
        if (bankAccountCountry?.code) {
            return deriveBankingMode(bankAccountCountry.code, companyCountryCode, countryFeatures.bankingStatusRules);
        }
        return bankingStatus === "foreign_local" || bankingStatus === "foreign_international"
            ? "international"
            : "local";
    })();

    // Backward-compat: if an existing beneficiary has intl_swift but no bankingStatus yet, set it
    const bankingModeInitialized = useRef(false);
    const intlSwift = watch("bankingMetadata.intl_swift" as any);
    useEffect(() => {
        if (bankingModeInitialized.current) return;
        bankingModeInitialized.current = true;
        if (intlSwift && bankingStatus === "local") setValue("bankingStatus", "foreign_local");
    }, [intlSwift]);

    // Reset routing number when IBAN country changes (IbanLocalFields shows/hides the field but doesn't clear the value)
    const iban = watch("iban")?.replace(/ /g, "");
    const isForeignIban = useMemo(() => iban && iban.length > 2 && !iban.toUpperCase().startsWith("FR"), [iban]);
    useEffect(() => {
        resetField("routingNumber");
    }, [isForeignIban]);

    // Accounting
    const usesAccountingCodeGroups = getCompanySettings("accounting_software") === "pennylane";
    const [filterValue, setFilterValue] = useState("");
    const filteredTypologies = useMemo(
        () => typologies?.filter((t) => t.toLowerCase().includes(filterValue.toLowerCase())),
        [typologies, filterValue],
    );
    const customFieldsOnCreation = customFields.filter((f) => f.showOnCreation);

    // Payment methods
    const isPaymentActive = (type: "rib" | "paypal" | "check") => acceptedPayments.includes(type);

    const onActiveAcceptedPayment = (e: React.ChangeEvent<HTMLInputElement>, type: "rib" | "paypal" | "check") => {
        if (e.target.checked) {
            setAcceptedPayments((prev) => [...prev, type]);
        } else {
            setAcceptedPayments((prev) => prev.filter((p) => p !== type));
            if (type === "rib") {
                setValue("iban", "");
                setValue("swift", "");
                setValue("bypassIbanValidation", false);
                setShowRibError(false);
            }
        }
        if (mode === "edit-page") setValue(type as any, undefined, { shouldDirty: true });
        clearErrors(["iban", "paypalEmail"]);
    };

    // Currency autocomplete
    const currenciesOptions = useMemo(() => {
        const options: TCurrencyData[] = [];
        [companyInfos?.currency, ...alternativeCurrencies].forEach((code) => {
            const found = currenciesList().find((c) => c.code === code);
            if (found) options.push(found);
        });
        return options;
    }, [alternativeCurrencies]);

    const renderInputCurrencies = (option: any) => {
        const flag = currenciesList().find((c) => c.code === option.inputProps.value)?.flag;
        return (
            <StyledTextField
                {...option}
                placeholder={t("NewTitle.SelectCurrency")}
                InputProps={{
                    ...option.InputProps,
                    startAdornment: (
                        <InputAdornment position="end">
                            <FlagTag
                                flagUrl={flag}
                                flagStyle={{
                                    width: 20,
                                    height: 20,
                                    opacity: 0.8,
                                    marginLeft: "0.25rem",
                                    display: "flex",
                                    alignItems: "center",
                                }}
                            />
                        </InputAdornment>
                    ),
                }}
            />
        );
    };

    return (
        <>
            <h3 className="form-section-title">
                {indexStep + 1}. {stepTitle}
            </h3>
            <span className="form-section-description">{stepSubtitle}</span>
            <div className="form-fields">
                {/** STATEMENT CURRENCY */}
                {usesMultipleCurrencies && (
                    <div className="form-accounting-payments-fields" style={{ marginBottom: "1rem" }}>
                        <TitleSeparation title={t("NewBeneficiary.StatementCurrency")} />
                        <AutocompleteFormComponent
                            options={currenciesOptions}
                            renderOptionText={(o: any) => o.code}
                            renderOption={(props: any, o: any) => (
                                <li {...props} key={o.code} className="currencies-title-options-list">
                                    <img src={o.flag} style={{ width: 20, height: 20 }} />
                                    <div className="currencies-info">
                                        <span style={{ fontSize: 12 }}>{o.name}</span>
                                        <span style={{ fontSize: 12 }}>{o.code}</span>
                                    </div>
                                </li>
                            )}
                            defaultFilterOptions
                            disableClearable
                            renderInput={renderInputCurrencies}
                            control={control}
                            controlName="statementCurrency"
                            error={errors.statementCurrency}
                        />
                    </div>
                )}

                {/** PAYMENT METHODS */}
                <div className="form-accounting-payments-fields" style={{ marginBottom: "1rem" }}>
                    <TitleSeparation title={t("NewBeneficiary.PaymentsMethods")} />
                    <div className="channels-wrapper">
                        {/** BANK TRANSFER */}
                        {companyAcceptedPayments.includes("rib") && (
                            <div className="channel-line-box">
                                <div className="channel-top">
                                    <div
                                        className="channel-title"
                                        style={{ opacity: isPaymentActive("rib") ? 1 : 0.5 }}
                                    >
                                        <Bank />
                                        <span>{t("NewBeneficiary.BankTransfer")}</span>
                                    </div>
                                    <div className="channel-switch-accounting" id="switch-rib">
                                        {!isPaymentActive("rib") &&
                                            beneficiaryData.acceptedPayments?.includes("rib") && (
                                                <div className="channel-reminder off">
                                                    <div className="chip off" />
                                                    {t("NewBeneficiary.ThisPaymentDeactivate")}
                                                </div>
                                            )}
                                        <LargeSwitch
                                            onChange={(e) => onActiveAcceptedPayment(e, "rib")}
                                            checked={isPaymentActive("rib")}
                                            disabled={!hasPermissions(["beneficiary:edit-rib"])}
                                        />
                                    </div>
                                </div>
                                {isPaymentActive("rib") && (
                                    <div className="form-payment-wrapper">
                                        {internationalBankingEnabled && (
                                            <BankAccountCountrySelector
                                                mode={mode}
                                                beneficiaryId={beneficiaryData.id}
                                            />
                                        )}
                                        <TextFormComponent
                                            label={t("NewBeneficiary.OwnerName")}
                                            control={control}
                                            error={errors.ribOwnerName}
                                            placeholder="Jean"
                                            controlName="ribOwnerName"
                                            disabled={!hasPermissions(["beneficiary:edit-rib"])}
                                        />
                                        <BankingFieldsSection
                                            bankingFields={countryFeatures.bankingFields}
                                            validationRules={countryFeatures.validation}
                                            disabled={!hasPermissions(["beneficiary:edit-rib"])}
                                            bankingMode={bankingMode}
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        {/** PAYPAL */}
                        {companyAcceptedPayments.includes("paypal") && (
                            <div className="channel-line-box">
                                <div className="channel-top">
                                    <div
                                        className="channel-title"
                                        style={{ opacity: isPaymentActive("paypal") ? 1 : 0.5 }}
                                    >
                                        <Paypal />
                                        <span>{t("NewBeneficiary.Paypal")}</span>
                                    </div>
                                    <div className="channel-switch-accounting" id="switch-paypal">
                                        {!isPaymentActive("paypal") &&
                                            beneficiaryData.acceptedPayments?.includes("paypal") && (
                                                <div className="channel-reminder off">
                                                    <div className="chip off" />
                                                    {t("NewBeneficiary.ThisPaymentDeactivate")}
                                                </div>
                                            )}
                                        <LargeSwitch
                                            onChange={(e) => onActiveAcceptedPayment(e, "paypal")}
                                            checked={isPaymentActive("paypal")}
                                        />
                                    </div>
                                </div>
                                {isPaymentActive("paypal") && (
                                    <div className="form-payment-wrapper" style={{ marginBottom: "1rem" }}>
                                        <TextFormComponent
                                            label={t("NewBeneficiary.PaypalEmail")}
                                            control={control}
                                            controlName="paypalEmail"
                                            error={errors.paypalEmail}
                                            required
                                            placeholder="example@crealo.app"
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        {/** BANK CHECK */}
                        {companyAcceptedPayments.includes("check") && (
                            <div className="channel-line-box">
                                <div className="channel-top">
                                    <div
                                        className="channel-title"
                                        style={{ opacity: isPaymentActive("check") ? 1 : 0.5 }}
                                    >
                                        <Check />
                                        <span>{t("NewBeneficiary.Check")}</span>
                                    </div>
                                    <div className="channel-switch-accounting" id="switch-check">
                                        {isPaymentActive("check") && (
                                            <div className="channel-reminder">
                                                <div className="chip" />
                                                {t("NewBeneficiary.PaymentCheckIsActive")}
                                            </div>
                                        )}
                                        <LargeSwitch
                                            onChange={(e) => onActiveAcceptedPayment(e, "check")}
                                            checked={isPaymentActive("check")}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/** ACCOUNTING */}
                <div className="form-accounting-payments-fields">
                    <TitleSeparation title={t("Beneficiars.AccountingInfo")} />
                    <div className="accounting-fields">
                        <AutocompleteFormComponent
                            label={t("Beneficiars.AccountingTypology")}
                            options={filteredTypologies || []}
                            renderInput={(params: any) => (
                                <StyledTextField
                                    {...params}
                                    error={!!errors.accountingTypology}
                                    helperText={errors.accountingTypology?.message ?? ""}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        setFilterValue(e.target.value)
                                    }
                                />
                            )}
                            renderOptionText={(o: any) =>
                                typeof o === "string" ? getAccountingTypologyName(o) : getAccountingTypologyName(o.name)
                            }
                            renderOption={(props: any, o: any) =>
                                o?.addElement ? (
                                    <li {...props} key={o.id} className="autocomplete-new-element">
                                        <div className="content">
                                            <Plus />
                                            {o.addElement}
                                        </div>
                                    </li>
                                ) : (
                                    <li {...props} key={o.id} className="beneficiary-language-option">
                                        <span>{getAccountingTypologyName(o)}</span>
                                    </li>
                                )
                            }
                            control={control}
                            controlName="accountingTypology"
                            addElement={t("Accounting.AddEntryType")}
                            required
                            customRequiredMessage={t("NewBeneficiary.RequiredTypology")}
                            error={errors.accountingTypology}
                            helpdeskInfo={{
                                text: t("Accounting.AccountingDoc"),
                                link: "https://helpdesk.crealo.app/fr/article/comment-creer-une-typologie-comptable-rfvo7w/",
                            }}
                        />
                        {usesAccountingCodeGroups ? (
                            <AccountingNumbersTableInput control={control as any} controlName="accountingCodeGroups" />
                        ) : (
                            <TextFormComponent
                                label={t("Beneficiars.AccountingCode")}
                                control={control}
                                controlName="accountingCode"
                            />
                        )}
                    </div>
                    {usesBrands && <BrandAccountingCodes brands={brands} />}
                    {customFields.length > 0 && (
                        <CustomFieldsCollapse
                            sectionFields={mode === "new" ? customFieldsOnCreation : customFields}
                            errors={errors}
                            mode={mode}
                        />
                    )}
                </div>
            </div>
        </>
    );
};

export default AccountingPaymentStep;
